import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { logActivity } from "@/lib/activityLogger";

interface ActivityLogExportProps {
  currentFilters: any;
}

export function ActivityLogExport({ currentFilters }: ActivityLogExportProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const exportToCSV = async () => {
    setLoading(true);
    try {
      // Fetch all logs with current filters (no pagination limit)
      const { data, error } = await supabase.functions.invoke('get-activity-logs', {
        body: {
          ...currentFilters,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          limit: 10000, // Max export limit
          offset: 0,
        },
      });

      if (error) throw error;

      const logs = data?.data || [];
      
      if (logs.length === 0) {
        toast.error('No logs to export');
        return;
      }

      // Convert to CSV
      const headers = ['Timestamp', 'User', 'Actor', 'Action Type', 'Category', 'Description', 'Resource Type', 'Resource ID', 'IP Address'];
      const csvRows = [headers.join(',')];

      logs.forEach((log: any) => {
        const row = [
          log.created_at,
          log.user_profile ? `"${log.user_profile.first_name} ${log.user_profile.last_name}"` : 'N/A',
          log.actor_profile ? `"${log.actor_profile.first_name} ${log.actor_profile.last_name}"` : 'System',
          log.action_type,
          log.action_category,
          `"${log.description.replace(/"/g, '""')}"`, // Escape quotes
          log.resource_type || 'N/A',
          log.resource_id || 'N/A',
          log.ip_address || 'N/A',
        ];
        csvRows.push(row.join(','));
      });

      // Download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `activity_logs_${format(new Date(), 'yyyy-MM-dd_HHmmss')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Log the export action
      await logActivity({
        actionType: 'data_exported',
        actionCategory: 'admin',
        description: 'Exported activity logs to CSV',
        metadata: { record_count: logs.length, filters: currentFilters },
      });

      toast.success(`Exported ${logs.length} logs successfully`);
      setOpen(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export Activity Logs</DialogTitle>
          <DialogDescription>
            Download activity logs as CSV. Optional date range filter (uses current filters if not specified).
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="export-start-date">Start Date (Optional)</Label>
            <Input
              id="export-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="export-end-date">End Date (Optional)</Label>
            <Input
              id="export-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          
          <p className="text-sm text-muted-foreground">
            Maximum 10,000 logs per export
          </p>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={exportToCSV} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Export
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}